import React, { useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { Check, X } from "lucide-react";
import { Storyboard } from "@/utils/types";

interface PasteOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPasteOriginal: () => Promise<Storyboard | null>;
  onCleanWithAI: (storyboard: Storyboard | null) => Promise<void>;
  htmlContent?: string;
}

export const PasteOptionsModal: React.FC<PasteOptionsModalProps> = ({
  isOpen,
  onClose,
  onPasteOriginal,
  onCleanWithAI,
  htmlContent,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [useAI, setUseAI] = useState(false);

  const handleInsert = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      // Always paste first
      const createdStoryboard = await onPasteOriginal();

      // If AI is enabled and a storyboard is created, also send enhancement request
      if (useAI && createdStoryboard) {
        await onCleanWithAI(createdStoryboard);
      }

      onClose();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isProcessing) {
      onClose();
    }
  };

  const getPreviewSrcDoc = () => {
    if (!htmlContent) return "";

    const isCode =
      htmlContent.includes("import ") ||
      htmlContent.includes("export default") ||
      htmlContent.includes('from "react"') ||
      (htmlContent.trim().startsWith("const") && htmlContent.includes("=>"));

    if (isCode) {
      return `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <style>
              html, body {
                background: transparent !important;
                margin: 0;
                padding: 0;
                height: 100%;
              }
              body { 
                padding: 12px; 
                font-family: 'Menlo', 'Monaco', 'Courier New', monospace; 
                font-size: 10px; 
                 color: #e5e7eb;
                white-space: pre-wrap; 
                word-break: break-word; 
              }
              ::-webkit-scrollbar { display: none; }
            </style>
          </head>
          <body>${htmlContent
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")}</body>
        </html>
      `;
    }

    let styles = "";
    // Try to find any active storyboard iframe to retrieve styles from
    // Priority order: Component storyboards -> Application storyboards
    const prioritySelectors = [
      "iframe.tempo-component-storyboard-iframe",
      "iframe.tempo-application-storyboard-iframe",
    ];

    for (const selector of prioritySelectors) {
      if (styles) break;

      const storyboardIframes = document.querySelectorAll(selector);

      // Find the first storyboard iframe that has stylesheets we can copy over
      for (const iframe of storyboardIframes) {
        try {
          const iframeEl = iframe as HTMLIFrameElement;
          // Check if we can access the content document
          if (iframeEl.contentDocument?.head) {
            const head = iframeEl.contentDocument.head;
            const styleTags = head.querySelectorAll(
              'style, link[rel="stylesheet"]',
            );
            const extractedStyles = Array.from(styleTags)
              .map((el) => el.outerHTML)
              .join("");

            if (extractedStyles) {
              styles = extractedStyles;
              break;
            }
          }
        } catch (e) {
          console.warn(
            "Could not access storyboard iframe styles, trying next:",
            e,
          );
        }
      }
    }

    // Fallback to editor styles if no storyboard found or access denied
    if (!styles) {
      styles = Array.from(
        document.querySelectorAll('link[rel="stylesheet"], style'),
      )
        .map((el) => el.outerHTML)
        .join("");
    }

    // Render HTML preview with dynamic scaling to fit entire content
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <base href="${window.location.origin}/" />
          <script src="https://cdn.tailwindcss.com"></script>
          ${styles}
          <style>
            html { background: transparent !important; }
            html, body { 
              margin: 0; 
              padding: 0; 
              width: 100%; 
              height: 100%; 
              overflow: hidden;
              background: transparent !important;
            }
            body {
              display: flex;
              align-items: center;
              justify-content: center;
              background: transparent !important;
            }
            #root {
              transform-origin: center center;
              max-width: none;
              max-height: none;
              background: transparent !important;
            }
            /* Basic resets to ensure visibility */
            * { box-sizing: border-box; }
            img, video, svg { max-width: 100%; height: auto; display: block; }
            h1, h2, h3, h4, h5, h6, p, ul, ol { margin-top: 0; }
            
            /* Hide scrollbars */
            ::-webkit-scrollbar { display: none; }
          </style>
        </head>
        <body>
          <div id="root">${htmlContent}</div>
          <style>html,body,#root{background:transparent!important}</style>
          <script>
            document.documentElement.style.cssText = 'background:transparent!important';
            document.body.style.cssText = 'background:transparent!important';
            
            (function() {
              function fitContent() {
                const root = document.getElementById('root');
                if (!root) return;
                
                // Get natural dimensions
                const contentWidth = root.scrollWidth;
                const contentHeight = root.scrollHeight;
                
                // Get available space (with some padding)
                const availableWidth = window.innerWidth - 32;
                const availableHeight = window.innerHeight - 32;
                
                // Calculate scale to fit
                const scaleX = availableWidth / contentWidth;
                const scaleY = availableHeight / contentHeight;
                const scale = Math.min(scaleX, scaleY, 1);
                
                // Apply transform
                root.style.transform = 'scale(' + scale + ')';
              }
              
              // Run immediately
              fitContent();
              // Also on load and resize
              window.addEventListener('load', fitContent);
              window.addEventListener('resize', fitContent);
            })();
          </script>
        </body>
      </html>
    `;
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="p-0 w-[420px] max-w-[420px] bg-[#131519] border border-[#3B3C42] gap-0 overflow-hidden rounded-2xl shadow-[0_20px_25px_-5px_rgba(0,0,0,0.6),0_8px_10px_-6px_rgba(0,0,0,0.6)]">
        <AlertDialogTitle className="sr-only">
          Insert Component
        </AlertDialogTitle>

        {/* Zone 1: Preview Stage */}
        <div className="relative bg-black p-10 flex items-center justify-center border-b border-[#3B3C42] bg-[radial-gradient(#2F3035_1px,transparent_1px)] bg-size-[20px_20px] min-h-[220px]">
          <span className="absolute top-3 left-3 text-[10px] text-[#808086] uppercase tracking-wide font-semibold">
            Captured Element
          </span>
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-[#808086] hover:text-white transition-colors cursor-pointer"
          >
            <X size={16} />
          </button>

          {htmlContent ? (
            <iframe
              srcDoc={getPreviewSrcDoc()}
              className="w-full max-w-[340px] h-[200px] border-0 pointer-events-none select-none"
              style={{
                backgroundColor: "transparent",
                background: "transparent",
              }}
              sandbox="allow-same-origin allow-scripts"
              title="Preview"
            />
          ) : (
            <div className="text-[#808086] text-xs">No preview available</div>
          )}
        </div>

        {/* Zone 2: Action Panel */}
        <div className="p-6 bg-[#131519]">
          <div className="mb-6">
            <h2 className="text-base font-semibold text-white mb-1">
              Insert Component
            </h2>
            <p className="text-[13px] text-[#9F9FA4]">
              Add this captured element to your canvas
            </p>
          </div>

          {/* AI Checkbox Row */}
          <div
            onClick={() => setUseAI(!useAI)}
            className={`flex gap-3 mb-6 items-start p-3 rounded-xl border cursor-pointer transition-all ${
              useAI
                ? "bg-[#2F3035] border-[#3B3C42]"
                : "bg-[#1F1F22] border-transparent hover:bg-[#2F3035]"
            }`}
          >
            <div className="relative w-[18px] h-[18px] shrink-0 mt-0.5">
              <div
                className={`absolute inset-0 rounded border-2 transition-all ${
                  useAI ? "bg-[#6448EF] border-[#6448EF]" : "border-[#808086]"
                }`}
              >
                {useAI && (
                  <Check
                    size={14}
                    className="text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                    strokeWidth={3}
                  />
                )}
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-1.5 text-[13px] font-medium text-white">
                Use AI to optimize my code
              </div>
              <p className="text-[11px] text-[#808086] mt-1 leading-snug">
                Rewrite and clean up the code after pasting it.
              </p>
            </div>
          </div>

          {/* Actions Footer */}
          <div className="flex items-center justify-end gap-3 mt-2">
            <Button
              variant="ghost"
              onClick={onClose}
              className="text-[13px] font-semibold text-[#9F9FA4] hover:text-white px-5 h-10 hover:bg-transparent"
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleInsert}
              disabled={isProcessing}
              isLoading={isProcessing}
              className="text-[13px] font-semibold h-10 px-5 rounded-lg shadow-[0_4px_10px_rgba(100,72,239,0.2)] transition-transform active:translate-y-0.5"
            >
              Insert Component
            </Button>
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
};
